import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded'
import { useTheme } from '@mui/material'

import { DiagnosisNoticePanel } from '../DiagnosisPrimitives/DiagnosisNoticePanel'

interface UnavailableDiagnosisCardProps {
  gameName: string
}

/**
 * Disclaimer surfaced under the "What's working, what's not?" section when
 * the game has no in-game (Game & Live Events) motivation profile on file.
 * Visually mirrors {@link PortfolioDiagnosisCard} so the section keeps the
 * same structure regardless of availability — only the copy and the icon
 * change.
 */
export const UnavailableDiagnosisCard: FC<UnavailableDiagnosisCardProps> = ({ gameName }) => {
  const { t } = useTranslation('ua-insights')
  const theme = useTheme()

  return (
    <DiagnosisNoticePanel
      accent={theme.palette.warning.main}
      icon={<InfoOutlinedIcon fontSize="small" />}
      title={t('diagnosis_unavailable_title')}
      body={t('diagnosis_unavailable_body', { game: gameName })}
      caption={t('diagnosis_unavailable_subtitle')}
    />
  )
}

interface PortfolioDiagnosisCardProps {
  text: string
  subtitle: string
}

/**
 * Lead-in card summarising the diagnosis. A subtle gradient + insights icon
 * elevate the panel so it reads as a "what we found" headline rather than a
 * generic note, without competing visually with the per-category columns
 * below.
 */
export const PortfolioDiagnosisCard: FC<PortfolioDiagnosisCardProps> = ({ text, subtitle }) => {
  const { t } = useTranslation('ua-insights')
  const theme = useTheme()

  return (
    <DiagnosisNoticePanel
      accent={theme.palette.primary.main}
      icon={<InsightsRoundedIcon fontSize="small" />}
      title={t('portfolio_diagnosis')}
      body={text}
      caption={subtitle}
      borderOpacity={0.2}
      backgroundOpacity={0.05}
      iconOpacity={0.14}
    />
  )
}
